import { useEffect, useCallback } from "react";
import { handleLogout } from "../../common/services/commonService";
import {
  isLongRunningTaskActive,
  subscribeLongRunningTasks,
} from "../../common/services/activityMonitor";

// Infosec requires a 30 minute inactivity timeout. The longer window applies
// only while the app is genuinely working on something the user started (an
// upload from local disk or SharePoint, a failed-file reprocess), so a transfer
// is not killed mid-flight just because nobody touched the mouse.
export const IDLE_TIMEOUT_MS = 30 * 60 * 1000;
export const LONG_TASK_TIMEOUT_MS = 120 * 60 * 1000;

const USER_ACTIVITY_EVENTS = [
  "mousedown",
  "mousemove",
  "keypress",
  "scroll",
  "touchstart",
];

export const InactivityLogout = ({
  inactivityTime = IDLE_TIMEOUT_MS,
  longRunningTaskInactivityTime = LONG_TASK_TIMEOUT_MS,
}) => {
  const logout = useCallback(() => {
    handleLogout(); // Call handleLogout to perform the logout action
  }, []);

  useEffect(() => {
    let inactivityTimer;
    let lastActivityAt = Date.now();

    const currentLimit = () =>
      isLongRunningTaskActive() ? longRunningTaskInactivityTime : inactivityTime;

    // Deadlines are compared against a timestamp rather than trusted to the
    // timer firing on schedule. A background tab is throttled and a sleeping
    // machine stops timers altogether, both of which would otherwise silently
    // extend the session past the limit.
    const checkDeadline = () => {
      const remaining = lastActivityAt + currentLimit() - Date.now();
      if (remaining <= 0) {
        logout();
        return;
      }
      inactivityTimer = setTimeout(checkDeadline, remaining);
    };

    const restartTimer = () => {
      clearTimeout(inactivityTimer);
      checkDeadline();
    };

    // Activity only stamps the clock; the pending timer re-arms itself from that
    // stamp when it fires. A burst of mousemove events therefore costs one
    // assignment each instead of tearing down and rebuilding a timer.
    const recordActivity = () => {
      lastActivityAt = Date.now();
    };

    const handleLongRunningTaskChange = (isActive) => {
      if (!isActive) {
        // Finishing counts as activity, so the user gets a full idle window from
        // completion instead of being logged out the moment a long upload lands.
        lastActivityAt = Date.now();
      }
      // The limit just changed, so the pending deadline is stale either way.
      restartTimer();
    };

    USER_ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, recordActivity);
    });
    const unsubscribe = subscribeLongRunningTasks(handleLongRunningTaskChange);

    restartTimer();

    return () => {
      USER_ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, recordActivity);
      });
      unsubscribe();
      clearTimeout(inactivityTimer);
    };
  }, [logout, inactivityTime, longRunningTaskInactivityTime]);

  return null;
};
